import { useEffect, useState } from 'react';
import {
  cacheRows,
  cachedRows,
  listRoomClaims,
  listRooms,
  loadSunshine,
  sunshineVisible,
  type Room,
  type RoomClaim,
  type StayGuest,
  type Sunshine,
} from '@dia/db';
import { supabase } from '../../supabase';
import { fetchForecast, readCachedForecast, type CachedForecast } from '../logic/weatherStore';

/**
 * The reads behind the home tiles. Every one is a source the app already has
 * (the stay row, the saved forecast, the rooms and claims), read network first
 * with the phone's copy as the fallback, so the home opens with no signal and
 * says what it last knew.
 */

/** The stay's own row (boat time). Same read and cache as Your stay used. */
export function useStayRow(stayId: string | undefined, online: boolean): StayGuest | null {
  const [stay, setStay] = useState<StayGuest | null>(null);

  useEffect(() => {
    if (!stayId) return;
    let live = true;
    (async () => {
      try {
        // stays_guest, not stays: a guest may never read notes_internal (migration 019).
        const { data, error } = await supabase.from('stays_guest').select('*').eq('id', stayId).maybeSingle();
        if (error) throw new Error(error.message);
        if (data) {
          await cacheRows('stays', [data as StayGuest]);
          if (live) setStay(data as StayGuest);
          return;
        }
      } catch {
        // No signal. Fall back to the last copy.
      }
      const cached = (await cachedRows<StayGuest>('stays')).find((s) => s.id === stayId);
      if (live && cached) setStay(cached);
    })();
    return () => {
      live = false;
    };
  }, [stayId, online]);

  return stay;
}

/** A saved forecast younger than this is shown as is, without asking again. */
const REFRESH_AFTER_MS = 30 * 60_000;

/**
 * The saved forecast, refreshed quietly when it is older than half an hour.
 * The Weather screen writes the same Dexie row, so either one keeps the other
 * current.
 */
export function useSavedWeather(online: boolean): { data: CachedForecast | null; now: number } {
  const [data, setData] = useState<CachedForecast | null>(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    let live = true;
    (async () => {
      const cached = await readCachedForecast();
      if (!live) return;
      if (cached) setData(cached);
      if (online && (!cached || Date.now() - cached.fetchedAt > REFRESH_AFTER_MS)) {
        const fresh = await fetchForecast();
        if (live && fresh) setData(fresh);
      }
      if (live) setNow(Date.now());
    })();
    const tick = setInterval(() => setNow(Date.now()), 60_000);
    return () => {
      live = false;
      clearInterval(tick);
    };
  }, [online]);

  return { data, now };
}

/** The bedrooms and this stay's claims, for "5 of 8 rooms picked". */
export function useRoomsAtAGlance(
  stayId: string | undefined,
  open: boolean,
  online: boolean,
): { rooms: Room[] | null; claims: RoomClaim[] | null } {
  const [rooms, setRooms] = useState<Room[] | null>(null);
  const [claims, setClaims] = useState<RoomClaim[] | null>(null);

  useEffect(() => {
    if (!stayId || !open) return;
    let live = true;
    Promise.all([listRooms(supabase), listRoomClaims(supabase, stayId)])
      .then(([roomRows, claimRows]) => {
        if (!live) return;
        setRooms(roomRows);
        setClaims(claimRows);
      })
      .catch(() => {
        // Neither read throws for being offline. Anything else leaves the
        // tile on its fixed line, which is still true.
      });
    return () => {
      live = false;
    };
  }, [stayId, open, online]);

  return { rooms, claims };
}

/**
 * Days the island has run on sun and batteries alone, or null.
 *
 * Null is the usual answer and it is a correct one: the tile only exists while
 * the count is a whole number of days and the data behind it is under 15 minutes
 * old (loadSunshine decides, from the server's own timestamp). Re-read every five
 * minutes while the home is open, and again when the signal returns.
 */
export function useSunshine(online: boolean): Sunshine | null {
  const [sunshine, setSunshine] = useState<Sunshine | null>(null);
  // The count is checked for age again every minute, not only when it is read:
  // one that was fine when it arrived must go when it turns stale.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), 60_000);
    // A phone that slept comes back with an old clock reading: refresh it the
    // moment the screen is visible again, not up to a minute later.
    const wake = () => {
      if (document.visibilityState !== 'hidden') setNow(Date.now());
    };
    document.addEventListener('visibilitychange', wake);
    return () => {
      clearInterval(tick);
      document.removeEventListener('visibilitychange', wake);
    };
  }, []);

  useEffect(() => {
    let live = true;
    const read = () => {
      loadSunshine(supabase)
        .then((s) => {
          if (live) setSunshine(s);
        })
        .catch(() => {
          // loadSunshine does not throw. Belt and braces: no tile.
          if (live) setSunshine(null);
        });
    };
    read();
    const timer = setInterval(read, 5 * 60_000);
    return () => {
      live = false;
      clearInterval(timer);
    };
  }, [online]);

  return sunshineVisible(sunshine, now) ? sunshine : null;
}
